import { useAuth } from "../../../context";
import { useMutation, useQuery } from "react-query";
import { DimensionAdminApi } from "../../../api/dimensions";
import { dimensionsKeys } from "./dimensions";

export const dimensionAdminKeys = {
  tree: () => ["dimensionTree"],
  dimensionItemUsage: (id) => ["dimensionItemUsage", id],
};

export function useGetDimensionTree(config) {
  const { callApi } = useAuth();
  return useQuery(dimensionAdminKeys.tree(), () => callApi(DimensionAdminApi.getDimensionTree), config);
}

export function useGetDimensionItemUsage(dimensionItemId, config) {
  const { callApi } = useAuth();
  return useQuery(
    dimensionAdminKeys.dimensionItemUsage(dimensionItemId),
    () => callApi(DimensionAdminApi.getDimensionItemUsage, dimensionItemId),
    { enabled: !!dimensionItemId, ...config },
  );
}

export function useUpdateDimension(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, data }) => callApi(DimensionAdminApi.updateDimension, { id, data }), config);
}

export function useDeleteDimension(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(DimensionAdminApi.deleteDimension, id), config);
}

export function useReactivateDimension(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(DimensionAdminApi.reactivateDimension, id), config);
}

export function useCreateDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation((data) => callApi(DimensionAdminApi.createDimensionItem, data), config);
}

export function useUpdateDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, data }) => callApi(DimensionAdminApi.updateDimensionItem, { id, data }), config);
}

export function useDeleteDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(DimensionAdminApi.deleteDimensionItem, id), config);
}

export function useReactivateDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(DimensionAdminApi.reactivateDimensionItem, id), config);
}

export function useAddDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ dimensionId, data }) => callApi(DimensionAdminApi.addDimensionItem, { dimensionId, data }),
    config,
  );
}

export function useAddChildDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ parentItemId, data }) => callApi(DimensionAdminApi.addChildDimensionItem, { parentItemId, data }),
    config,
  );
}

export function useLinkToParent(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ parentId, childId }) => callApi(DimensionAdminApi.linkToParent, { parentId, childId }),
    config,
  );
}

export function useUnlinkFromParent(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ parentId, childId }) => callApi(DimensionAdminApi.unlinkFromParent, { parentId, childId }),
    config,
  );
}

export function useRenameDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, name }) => callApi(DimensionAdminApi.renameDimensionItem, { id, name }), config);
}

export function useRenameDimension(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, name }) => callApi(DimensionAdminApi.renameDimension, { id, name }), config);
}

export function useGetMergePreview(sourceId, targetId, config) {
  const { callApi } = useAuth();
  return useQuery(
    ["mergePreview", sourceId, targetId],
    () => callApi(DimensionAdminApi.getMergePreview, { sourceId, targetId }),
    { enabled: !!sourceId && !!targetId, ...config },
  );
}

export function useMergeDimensionItem(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ sourceId, targetId }) => callApi(DimensionAdminApi.mergeDimensionItem, { sourceId, targetId }),
    config,
  );
}
